import React from 'react';
import HighlightIcon from '../HighlightIcon';
import { LockOutlined, PictureOutlined } from '@ant-design/icons';
import { checkShowFull } from '../SearchList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudSun, faUmbrella, faKey, faCarSide, faBusAlt, faMapMarkerAlt, faBed } from '@fortawesome/free-solid-svg-icons';

type Highlight2Field = {
  value: string;
  notes: string;
  text: string;
};

type Highlights2Object = Record<string, Highlight2Field>;

type HighlightItem = {
  check?: boolean;
  type?: string;
  description?: string;
};

type Highlight2Item = {
  structfield_key?: string;
  structfield_value?: string;
  structfield_notes?: string;
};

type NewCardHighlightBProps = {
  highlights?: HighlightItem[];
  highlights2?: Highlight2Item[];
  facility: any;
  photosCount?: number;
  onPhotosClick?: (e: React.MouseEvent) => void;
  isParkingAt?: boolean;
  hideShuttle?: boolean;
  airportInitials?: string;
  airportDisplayType?: number;
  [key: string]: any;
};

const INVALID_VALUES = ['not specified', 'n/a', 'na'];

const normalize = (value = '') => {
  const text = `${value}`.trim();

  return INVALID_VALUES.includes(text.toLowerCase()) ? '' : text;
};

function buildHighlights2Object(highlights2 = []): Highlights2Object {
  if (!Array.isArray(highlights2)) {
    return {};
  }

  const byKey: Highlights2Object = {};

  for (const item of highlights2) {
    const key = `${item?.structfield_key || ''}`.trim();

    if (!key) continue;

    const value = `${item?.structfield_value || ''}`.trim();
    const notes = `${item?.structfield_notes || ''}`.trim();

    byKey[key] = {
      value,
      notes,
      text: [value, notes].filter(Boolean).join(' ')
    };
  }

  return byKey;
}

export default function NewCardHighlightB({
  highlights = [],
  highlights2 = [],
  facility,
  photosCount = 0,
  onPhotosClick,
  isParkingAt,
  isCheckoutSummary,
  hideShuttle = false,
  airportInitials,
  airportDisplayType,
  ...props
}: NewCardHighlightBProps) {
  const checkedHighlights = highlights.filter(
    (highlight) => highlight.check
  );
  const getHighlight = (type) =>
    checkedHighlights.find((h) => h.type === type);

  const freeShuttle = getHighlight('facility_free_shuttles');

  const hasFreeShuttle = checkedHighlights.some(
    (highlight) => highlight.type === 'facility_free_shuttles'
  );

  const hasNoShuttle = checkedHighlights.some(
    (highlight) => highlight.type === 'facility_no_shuttles'
  );

  const getParkingType = () => {
    const type = facility.facility_product_type?.toLowerCase() || '';

    if (!type) {
      return null;
    }

    if (type.includes('valet')) {
      return {
        label: 'Valet',
        icon: faKey,
      };
    }

    if (type.includes('self') && !type.includes('valet')) {
      return {
        label: 'Self Park',
        icon: faCarSide,
      };
    }

    return null;
  };

  const getCoverageType = () => {
    const type = facility.facility_product_type?.toLowerCase() || '';

    if (!type) {
      return null;
    }

    if (
      type.includes('covered') &&
      !type.includes('uncovered')
    ) {
      return {
        label: 'Covered',
        icon: faUmbrella,
      };
    }

    if (type.includes('uncovered')) {
      return {
        label: 'Uncovered',
        icon: faCloudSun,
      };
    }

    return null;
  };
  const parkingType = getParkingType();
  const coverageType = getCoverageType();


  const highlightMap = buildHighlights2Object(highlights2);

  const hasHighlights2 = Object.keys(highlightMap).length > 1;

  const getText = (key) => normalize(highlightMap[key]?.value);

  const getNotes = (key) => normalize(highlightMap[key]?.notes);

  const shuttleAvailability = getText('shuttle_availability').toLowerCase();

  const shuttleFrequencyValue =
    getText('shuttle_frequency');

  const shuttleFrequency =
    shuttleFrequencyValue.toLowerCase() === 'other'
      ? ""
      : shuttleFrequencyValue;

  const shuttleSchedule = getText('shuttle_schedule');

  const dtcType = getText('dtc_type');

  const shuttleStartTime = getText('shuttle_start_time');

  const shuttleEndTime = getText('shuttle_end_time');

  const shuttleCostIncluded =
    getText('shuttle_cost_included').toLowerCase();


  const facilityType =
    getText('facility_type') ||
    getText('facility_parking_type') ||
    facility.facility_parking_type ||
    '';

  const facilityTypeLabel =
    (airportDisplayType ?? facility?.Airport?.airport_display_type) == 2
      ? facilityType.replace(/on airport parking facility/i, 'On Cruise Parking Facility')
      : facilityType;

  const parkingCondition = getText('parking_condition');

  const parkingLocation = getText('parking_location');

  const parkingConditionText = parkingCondition.toLowerCase();

  const parkingLocationText = parkingLocation.toLowerCase();

  const parkingConditionIcon = parkingConditionText.includes('valet')
    ? faKey
    : faCarSide;

  const parkingConditionLabel = parkingConditionText.includes('valet')
    ? 'Valet Park'
    : parkingConditionText.includes('self')
      ? 'Self Park'
      : parkingCondition;

  const parkingLocationIcon =
    parkingLocationText.includes('covered') &&
      !parkingLocationText.includes('uncovered')
      ? faUmbrella
      : faCloudSun;

  const parkingLocationLabel = /^covered/i.test(parkingLocation)
    ? 'Covered'
    : /^uncovered/i.test(parkingLocation)
      ? 'Uncovered'
      : parkingLocation;

  const isUnbranded = hasHighlights2
    ? dtcType.toLowerCase() === 'unbranded'
    : facility.facility_branded !== 1;

  const showFacilityTypeInsteadOfDistance =
    hasHighlights2 &&
    facilityType.toLowerCase().includes('on airport parking facility') || facilityType.toLowerCase().includes('on cruise parking facility');

  const hasFreeShuttle2 = shuttleAvailability === 'yes';

  const hasNoShuttle2 = shuttleAvailability === 'no';

  const hasLimitedShuttle2 = shuttleAvailability === 'limited';

  const hasNotRequiredShuttle2 =
    shuttleAvailability === 'not required';

  const hasFreeShuttleToShow = hasHighlights2
    ? hasFreeShuttle2
    : hasFreeShuttle;

  const hasNoShuttleToShow = hasHighlights2
    ? hasNoShuttle2 || hasNotRequiredShuttle2
    : hasNoShuttle;

  const hasLimitedShuttleToShow = hasHighlights2
    ? hasLimitedShuttle2
    : false;

  const shuttleScheduleLabel =
    shuttleSchedule.toLowerCase() === 'fixed hours'
      ? [shuttleStartTime, shuttleEndTime].filter(Boolean).join(' - ')
      : shuttleSchedule;

  const shuttleVehicleValue = getText('shuttle_vehicle');

  const shuttleVehicleLabel = [
    shuttleVehicleValue
  ]
    .filter(Boolean)
    .join(' ');

  const shuttleDescription = [
    shuttleFrequency,
    shuttleScheduleLabel,
    shuttleVehicleLabel,

    shuttleCostIncluded === 'yes'
      ? `Shuttle Cost Included`
      : 'Shuttle Cost Not Included',
  ]
    .filter(Boolean)
    .join(' · ');


  const limitedDetails = hasLimitedShuttle2 ? 'Yes + Conditions' : '';

  const legacyShuttleDescription =
    `${freeShuttle?.description || ''}`.trim();

  const freeShuttleDescriptionToShow = hasHighlights2
    ? shuttleDescription
    : legacyShuttleDescription;

  const limitedShuttleDescriptionToShow = limitedDetails;

  // Same shuttle description composition as WithShuttles.tsx, used only for the parkingat card
  const shuttleFrequencyWithShuttles =
    shuttleFrequencyValue.toLowerCase() === 'other'
      ? getNotes('shuttle_frequency')
      : shuttleFrequencyValue;
  const shuttleVehicleCapacity = getText('shuttle_vehicle_capacity');
  const shuttleContinuousValue = getText('shuttle_continuous').toLowerCase();
  const shuttleContinuousNotes = getNotes('shuttle_continuous');
  const shuttleContinuousLabel =
    shuttleContinuousValue === 'upon request'
      ? shuttleContinuousNotes || 'Upon Request'
      : '';
  const shuttleAvailabilityNotes = getNotes('shuttle_availability');
  const shuttleCostIncludedNotes = getNotes('shuttle_cost_included');
  const shuttleVehicleLabelWithShuttles = [
    shuttleVehicleValue,
    shuttleVehicleCapacity && `(${shuttleVehicleCapacity} seats)`,
  ]
    .filter(Boolean)
    .join(' ');

  const noShuttleDescriptionWithShuttles = 'No shuttle available';

  const limitedShuttleDescriptionWithShuttles = [
    'Limited Shuttle',
    shuttleVehicleLabelWithShuttles,
    shuttleContinuousLabel,
    shuttleAvailabilityNotes,
  ]
    .filter(Boolean)
    .join(' - ');

  const freeShuttleDescriptionWithShuttles = [
    shuttleFrequencyWithShuttles,
    shuttleScheduleLabel,
    shuttleVehicleLabelWithShuttles,
    shuttleContinuousLabel,
    shuttleCostIncluded === 'yes' ? 'Shuttle Cost Included' : shuttleCostIncludedNotes,
  ]
    .filter(Boolean)
    .join(' - ');

  const freeShuttleDescriptionFinal = isParkingAt && hasHighlights2
    ? freeShuttleDescriptionWithShuttles
    : freeShuttleDescriptionToShow;

  const limitedShuttleDescriptionFinal = isParkingAt && hasHighlights2
    ? limitedShuttleDescriptionWithShuttles
    : limitedShuttleDescriptionToShow;

  const shouldShowFull =
    checkShowFull(facility.facility_url_code);

  const shouldShowDistanceBlock =
    showFacilityTypeInsteadOfDistance ||
    shouldShowFull;

  const numericDistance = Number(facility.facility_airport_distance);
  const hasDistance = Number.isFinite(numericDistance) && numericDistance > 0;

  const distanceLabel =
    showFacilityTypeInsteadOfDistance
      ? facilityTypeLabel
      : isUnbranded
        ? `Approx. ${facility.facility_airport_distance} mi from terminal`
        : `${facility.facility_airport_distance} mi from terminal`;

  const isHotel = facility.facilityParkingType == 3

  const chipBaseClass =
    'inline-flex items-center gap-1.5 text-xs whitespace-nowrap rounded-full bg-[#E8ECF1] px-2.5 py-1 font-semibold text-[#334155]';

  const lineBaseClass = 'mt-[7px] flex items-start gap-1.5 leading-[1.35] text-[#1E2A38]';

  return (
    <div className={`flex flex-col pb-2 font-['Helvetica_Neue',_Arial,_sans-serif]`}>
      {isUnbranded && (!isParkingAt || isCheckoutSummary) && (
        <span className="mt-0.5 whitespace-nowrap font-bold text-primary">Special Rate - {airportInitials ?? facility?.airport_initials}</span>
      )}
      {hasHighlights2 ? (
        (parkingCondition || parkingLocation) && (
          <div className="mt-[7px] flex flex-wrap gap-1.5">
            {parkingCondition && (
              <span className={chipBaseClass}>
                <FontAwesomeIcon
                  icon={parkingConditionIcon}
                  {...props}
                  style={{ width: 12, height: 12 }}
                />
                {parkingConditionLabel}
              </span>
            )}
            {parkingLocation && (
              <span className={chipBaseClass}>
                <FontAwesomeIcon
                  icon={parkingLocationIcon}
                  {...props}
                  style={{ width: 12, height: 12 }}
                />
                {parkingLocationLabel}
              </span>
            )}
            {isHotel && !isParkingAt && (
              <span className={chipBaseClass}>
                <FontAwesomeIcon
                  icon={faBed}
                  {...props}
                  style={{ width: 12, height: 12 }}
                />
                Hotel + Parking
              </span>
            )}
          </div>
        )
      ) : (
        (parkingType || coverageType) && (
          <div className="mt-[7px] flex flex-wrap gap-1.5">
            {parkingType && (
              <span className={chipBaseClass}>
                <FontAwesomeIcon
                  icon={parkingType.icon}
                  {...props}
                  style={{ width: 12, height: 12 }}
                />
                {parkingType.label}
              </span>
            )}
            {coverageType && (
              <span className={chipBaseClass}>
                <FontAwesomeIcon
                  icon={coverageType.icon}
                  {...props}
                  style={{ width: 12, height: 12 }}
                />
                {coverageType.label}
              </span>
            )}
            {isHotel && !isParkingAt && (
              <span className={chipBaseClass}>
                <FontAwesomeIcon
                  icon={faBed}
                  {...props}
                  style={{ width: 12, height: 12 }}
                />
                Hotel + Parking
              </span>
            )}
          </div>
        )
      )}

      {isUnbranded && !isParkingAt && (
        <div className="mt-[7px] flex items-start gap-1.5 text-[12px] leading-[1.35] text-[#1866BF]">
          <span>
            <LockOutlined className="w-3.5 h-3.5" />
          </span>
          <span className="text-[12.5px] leading-tight">
            Location details will be shared after booking.
          </span>
        </div>
      )}

      {!hideShuttle && (
        <div className={`flex flex-col ${isParkingAt ? 'md:flex-row md:flex-wrap md:gap-x-3 md:gap-y-1' : ''}`}>
          {hasFreeShuttleToShow ? (
            <div className={`${lineBaseClass} w-fit max-w-full mt-[7px]`}>
              <span className="mt-[2px]">
                <FontAwesomeIcon
                  icon={faBusAlt}
                  {...props}
                  style={{ width: 13, height: 13 }}
                  className="text-[#1f9d57]"
                />
              </span>
              <span className="min-w-0">
                Shuttle
                {freeShuttleDescriptionFinal && (
                  <span className="text-[#434343]"> · {freeShuttleDescriptionFinal}</span>
                )}
              </span>
            </div>
          ) : hasLimitedShuttleToShow ? (
            <div className={`${lineBaseClass} w-fit max-w-full mt-[7px]`}>
              <span className="mt-[2px]">
                <FontAwesomeIcon
                  icon={faBusAlt}
                  {...props}
                  style={{ width: 13, height: 13 }}
                  className="text-[#1f9d57]"
                />
              </span>
              <span className="min-w-0">
                Shuttle
                {limitedShuttleDescriptionFinal && (
                  <span className="text-[#434343]"> · {limitedShuttleDescriptionFinal}</span>
                )}
              </span>
            </div>
          ) :
            hasNoShuttleToShow && (
              <div className={`${lineBaseClass} w-fit max-w-full mt-[7px]`}>
                <span className="mt-[2px]">
                  <HighlightIcon
                    type="no-free-shuttle"
                    width={14}
                    height={14}
                    fill="#ef4444"
                  />
                </span>
                <span className="min-w-0 text-red-500">
                  {hasHighlights2 && hasNotRequiredShuttle2
                    ? 'No shuttle required'
                    : isParkingAt && hasHighlights2
                      ? noShuttleDescriptionWithShuttles
                      : 'No shuttle service'}
                </span>
              </div>
            )}

          {shouldShowDistanceBlock && (showFacilityTypeInsteadOfDistance || hasDistance) && (
            <div className={lineBaseClass}>
              <span className="mt-[2px]">
                <FontAwesomeIcon
                  icon={faMapMarkerAlt}
                  {...props}
                  style={{ width: 13, height: 13 }}
                  className="text-[#526171]"
                />
              </span>
              <span className="min-w-0">
                {distanceLabel}
              </span>
            </div>
          )}

          {isUnbranded && isParkingAt && (
            <div className={`${lineBaseClass}`}>
              <span>
                <LockOutlined className="w-3.5 h-3.5 text-[#1866BF]" />
              </span>
              <span className="min-w-0">
                Address revealed after booking
              </span>
            </div>
          )}

          {photosCount > 0 && !isUnbranded && (
            <div
              className={`${lineBaseClass} ${onPhotosClick ? 'cursor-pointer hover:underline' : ''}`}
              onClick={onPhotosClick}
            >
              <span className="mt-[2px]">
                <PictureOutlined style={{ fontSize: 13 }} className="text-[#1866BF]" />
              </span>
              <span className="min-w-0 text-[#1866BF]">
                Photos ({photosCount})
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
